import { useState } from 'react'
import { Link } from '@tanstack/react-router'
import {
  ArrowRight,
  Bell,
  CalendarClock,
  Landmark,
  ShieldCheck,
  UserRound,
  Webhook,
} from 'lucide-react'
import { Button, Card, StatusBadge } from '@shared/design-system/ui'
import { useFeatureFlag } from '@shared/feature-flags/useFeatureFlag'
import { useSession } from '@shared/lib/auth/session-store'
import { useWebhooksQuery } from '@entities/webhook'
import {
  ReceivingPlanChangeDrawer,
  useReceivingPlanQuery,
  planName,
} from '@features/receiving-plan'
import {
  actionLink,
  cardHeader,
  cardIcon,
  cardText,
  grid,
  page,
  sectionCopy,
  sectionTitle,
  subtitle,
  title,
} from './ConfigPage.css'
import { useEnrollment } from '@features/onboarding-kyc/api/enrollment'
import { useKycVerification } from '@features/onboarding-kyc/api/verification'
import { onboardingStatus } from '@features/onboarding-kyc/model/onboarding-status'
import {
  accountPresentation,
  bankCardPresentation,
  registrationActionLabel,
} from './config-account-status'

export function ConfigPage() {
  const emailPreferencesEnabled = useFeatureFlag('emailNotificationPreferences')
  const user = useSession((state) => state.user)
  const webhooksQuery = useWebhooksQuery()
  // The onboarding view exists only once the account is linked; before that the cards ask to register.
  const enrollment = useEnrollment()
  const onboardingView = useKycVerification(enrollment.data?.status === 'account_linked')
  const operationalPresentation = accountPresentation({
    isSaleBlocked: user?.isSaleBlocked,
    documentationStatus: user?.documentationStatus,
    operations: onboardingView.data?.onboarding.operations,
  })
  // Same badge as the onboarding page, so both screens always show the same registration status.
  const registrationStatus = onboardingStatus(onboardingView.data?.onboarding)
  const registrationAction = registrationActionLabel(onboardingView.data?.onboarding.badge)
  const bankCard = bankCardPresentation(
    onboardingView.data?.bank?.accounts.find((account) => account.role === 'active'),
  )
  const [planDrawerOpen, setPlanDrawerOpen] = useState(false)
  const planQuery = useReceivingPlanQuery()
  const currentPlan = planQuery.data?.current
  const activeWebhooks = (webhooksQuery.data ?? []).filter(
    (webhook) => webhook.status === 'ACTIVE',
  ).length

  return (
    <div className={page} data-testid="config-page">
      <div>
        <h1 className={title}>Configurações</h1>
        <p className={subtitle}>Perfil, segurança e dados necessários para operar sua conta.</p>
      </div>

      <div className={grid}>
        <Card>
          <div className={cardHeader}>
            <span className={cardIcon}>
              <UserRound size={18} />
            </span>
            <div>
              <h2 className={sectionTitle}>Perfil</h2>
              <p className={sectionCopy}>{user?.email ?? 'Conta VendePay'}</p>
            </div>
          </div>
          <p className={cardText}>
            Dados pessoais e preferências ficam concentrados aqui. O cadastro fiscal segue o fluxo
            guiado de recebimento.
          </p>
        </Card>

        <Card>
          <div className={cardHeader}>
            <span className={cardIcon}>
              <ShieldCheck size={18} />
            </span>
            <div>
              <h2 className={sectionTitle}>Cadastro e documentação</h2>
              <p className={sectionCopy}>
                {operationalPresentation.tone === 'success'
                  ? 'Documentação fiscal e do responsável.'
                  : 'Conta com restrições operacionais.'}
              </p>
            </div>
            <StatusBadge tone={registrationStatus.tone} label={registrationStatus.label} />
          </div>
          <p className={cardText}>{operationalPresentation.description}</p>
          <Link to="/onboarding" className={actionLink}>
            {registrationAction}
            <ArrowRight size={16} />
          </Link>
        </Card>

        <Card>
          <div className={cardHeader}>
            <span className={cardIcon}>
              <Landmark size={18} />
            </span>
            <div>
              <h2 className={sectionTitle}>Conta bancária</h2>
              <p className={sectionCopy}>Gerencie a conta que recebe os repasses.</p>
            </div>
          </div>
          <p className={cardText}>{bankCard.text}</p>
          <Link to="/onboarding" hash="conta-bancaria" className={actionLink}>
            {bankCard.action}
            <ArrowRight size={16} />
          </Link>
        </Card>

        <Card>
          <div className={cardHeader}>
            <span className={cardIcon}>
              <CalendarClock size={18} />
            </span>
            <div>
              <h2 className={sectionTitle}>Plano comercial</h2>
              <p className={sectionCopy}>Condições para pagamentos com cartão.</p>
            </div>
            <StatusBadge
              tone={currentPlan ? 'success' : 'neutral'}
              label={currentPlan ? planName(currentPlan) : 'Consultar plano'}
            />
          </div>
          <p className={cardText}>
            Consulte as taxas de venda, a reserva e os prazos de liberação do saldo por meio de
            pagamento. Esses prazos não confirmam depósito bancário. As condições do plano valem
            para novas vendas; recebíveis já gerados mantêm as condições originais.
          </p>
          <div className={cardText}>
            <Button variant="secondary" onClick={() => setPlanDrawerOpen(true)}>
              Ver plano e taxas
            </Button>
          </div>
        </Card>

        <Card>
          <div className={cardHeader}>
            <span className={cardIcon}>
              <Webhook size={18} />
            </span>
            <div>
              <h2 className={sectionTitle}>Webhooks</h2>
              <p className={sectionCopy}>Eventos da conta para n8n, CRMs e áreas de membros.</p>
            </div>
            <StatusBadge
              tone={activeWebhooks > 0 ? 'success' : 'neutral'}
              label={activeWebhooks > 0 ? `${activeWebhooks} ativo(s)` : 'Configurar'}
            />
          </div>
          <p className={cardText}>
            Configure endpoints, envie testes assinados e acompanhe tentativas de entrega.
          </p>
          <Link to="/config/webhooks" className={actionLink}>
            Abrir webhooks
            <ArrowRight size={16} />
          </Link>
        </Card>
        {emailPreferencesEnabled && (
          <Card data-testid="email-notifications-card">
            <div className={cardHeader}>
              <span className={cardIcon}>
                <Bell size={18} />
              </span>
              <div>
                <h2 className={sectionTitle}>Notificações</h2>
                <p className={sectionCopy}>Preferências de email da sua conta.</p>
              </div>
            </div>
            <p className={cardText}>
              Escolha quais emails de vendas, assinaturas e recebimentos você recebe.
            </p>
            <Link
              to="/config/notificacoes"
              className={actionLink}
              data-testid="email-notifications-open-btn"
            >
              Configurar notificações
              <ArrowRight size={16} />
            </Link>
          </Card>
        )}
      </div>

      <ReceivingPlanChangeDrawer open={planDrawerOpen} onClose={() => setPlanDrawerOpen(false)} />
    </div>
  )
}
